import { createContext, useState, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import createAuthorizationClient from '@izettle/authorization-sdk-js';
import { useInitialization } from '@izettle/react';
import { withScope, setTag } from '@sentry/react';
import { useConfig } from '../hooks/use-config.js';
import { useBaseService } from '../hooks/use-service.js';
import { KnownService } from './services.js';
import { jsx } from 'react/jsx-runtime';

const removeDuplicates = arr => Array.from(new Set(arr));
const removeTrailingSlash = str => str.endsWith("/") ? str.slice(0, str.length - 1) : str;
const getRedirectionURI = () => `${window.location.origin}${removeTrailingSlash(__webpack_public_path__)}`;
const defaultPush = path => window.history.pushState({}, "iZettle", path);

// We Omit nothing from AuthClient here to convert the type from a class to an object.

const handleOneTimeToken = (params, loginUrl) => {
  const oneTimeToken = params.get("one_time_token");

  // We have to remove the OTT from the original URL so we don't get stuck in a redeeming loop
  params.delete("one_time_token");
  const newParams = params.toString();
  let originalUrl = window.location.origin + window.location.pathname;
  if (newParams.length > 0) {
    originalUrl += `?${newParams}`;
  }
  const redirection = `${loginUrl}/redeem_one_time_token/${oneTimeToken}?redirectTo=${originalUrl}`;
  window.location.assign(encodeURI(redirection));
};
const handleOAuthCallback = (client, setState, pushPath) => client.handleRedirectCallback().then(_ref => {
  let {
    appState: persistedState
  } = _ref;
  // For asserting in tests
  if (process.env.NODE_ENV === "test") {
    window.history.pushState({}, "Authenticated");
  }
  if (persistedState) {
    if (persistedState.originalPath) {
      pushPath(persistedState.originalPath);
    }
    setState(persistedState);
  }
});

/**
 * Creates an errorHandler that adds `originalUrl` to the `appState`.
 */
const createErrorHandler = (client, fn) => (_error, options) => client[fn]({
  ...options,
  appState: {
    ...(options.appState ?? {}),
    originalPath: `${window.location.pathname}${window.location.search}`
  }
});

/* istanbul ignore next */
/**
 * Maps functions from the client to easily overridable and destructurable fields.
 *
 * Overrides some fields to add extra functionality, like `getToken` and its `originalUrl`
 */
const createData = function (client) {
  let state = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let isImpersonatedUser = arguments.length > 2 ? arguments[2] : undefined;
  if (client == null) {
    return null;
  }
  return {
    state,
    isImpersonatedUser,
    getToken: async function (options) {
      let {
        loginRequiredHandler,
        interactionRequiredHandler
      } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return client.getToken(options, {
        loginRequiredHandler: loginRequiredHandler ?? createErrorHandler(client, "loginWithRedirect"),
        interactionRequiredHandler: interactionRequiredHandler ?? createErrorHandler(client, "verifyWithRedirect")
      });
    },
    getTokenSilently: async function () {
      return client.getTokenSilently(...arguments);
    },
    getUser: async function () {
      return client.getUser(...arguments);
    },
    isAuthenticated: async function () {
      return client.isAuthenticated(...arguments);
    },
    handleRedirectCallback: async function () {
      return client.handleRedirectCallback(...arguments);
    },
    loginWithRedirect: async function () {
      return client.loginWithRedirect(...arguments);
    },
    logout: function () {
      return client.logout(...arguments);
    },
    verifyWithPopUp: async function () {
      return client.verifyWithPopUp(...arguments);
    },
    verifyWithRedirect: async function () {
      return client.verifyWithRedirect(...arguments);
    }
  };
};
const AuthContext = /*#__PURE__*/createContext(null);
const AuthProvider = _ref2 => {
  let {
    children
  } = _ref2;
  const {
    config
  } = useConfig();
  // We can only use `useBaseServices` in here since it's what's available.
  const oauthUrl = useBaseService(KnownService.Auth);
  const loginUrl = useBaseService(KnownService.Login);
  const history = useHistory();
  const [client, setClient] = useState(null);
  const [state, setState] = useState(null);
  const [isImpersonatedUser, setIsImpersonatedUser] = useState(false);
  const [fnWrappers, setFnWrappers] = useState(null);
  const [hasToken, setHasToken] = useState(false);

  // I added `withScope` here since I think the tag will be set for _everything_
  // reported after it if we don't do it.
  const baseHandleError = useErrorHandler();
  const handleError = error => {
    withScope(() => {
      setTag("auth-sdk", true);
      baseHandleError(error);
    });
  };
  const scopes = removeDuplicates(["ALL:INTERNAL", ...(config.oauth.extraScopes ?? [])]);

  // Create a client on initialization
  useEffect(() => {
    void createAuthorizationClient({
      domain: new URL(oauthUrl).host,
      client_id: config.oauth.clientId,
      redirect_uri: getRedirectionURI(),
      scope: scopes.join(" ")
    }).then(newClient => setClient(newClient)).catch(handleError);
  }, []);

  // Run auth flow stuff when client starts existing
  useInitialization(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("one_time_token") != null) {
      handleOneTimeToken(params, loginUrl);
      return;
    }
    if (params.get("code") != null) {
      // We need to have a default value for `push` here in case there is no react-router-dom Context.
      void handleOAuthCallback(fnWrappers, setState, history?.push ?? defaultPush).then(() => setHasToken(true)).catch(err => {
        // Handle login flows expiring due to inactivity (e.g. internet loss)
        if (err.message === "Invalid state") {
          // Re-do login flow - if they successfully logged in we'll just get a new token
          // Otherwise they'll get sent to the login pages again! :D
          return client?.loginWithRedirect();
        }
        handleError(err);
      });
      return;
    }
    void fnWrappers.getToken().then(token => {
      if (token != null) {
        setHasToken(true);
        return fnWrappers.getUser();
      }
    }).then(userClaims => {
      setIsImpersonatedUser(userClaims?.impersonator_uuid != null);
    }).catch(handleError);
  }, fnWrappers);

  // Update data
  useEffect(() => {
    setFnWrappers(createData(client, state, isImpersonatedUser));
  }, [client, state, isImpersonatedUser]);
  useEffect(() => {
    window.authorizationClient = client;
  }, [client]);
  return fnWrappers != null && hasToken ? /*#__PURE__*/jsx(AuthContext.Provider, {
    value: fnWrappers,
    children: children
  }) : null;
};

export { AuthContext, AuthProvider, getRedirectionURI };
//# sourceMappingURL=auth.js.map
